import React, { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { Location } from "history";
import { TFunction, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RouteComponentProps, useHistory, useLocation } from "react-router";
import { Link } from "react-router-dom";
import { apiOrganization } from "src/services/Api/Organization/apiOrganization";
import { wrapPromise } from "go-core";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import Tag from "go-app/components/Tags/Tag";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationState } from "go-security/services/organizations/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext, SegmentListService } from "go-segment/context";
import { StocktakingApi } from "../../../../../../../../services/Api/Organization/types";
import StocktakingStatusLabel from "../components/StocktakingStatusLabel";

const TranslationsInitialized = () => {
	const { t } = useTranslation();
	t("enums.stocktaking.type.VALUE");
	t("enums.stocktaking.type.AVAILABILITY");
};

interface ListState {
	resource?: Record<string, any>;
	path: string;
	mobileActions: MobileActionProps[];
}

const listName = "STOCKTAKING";
const resourceType = "STOCKTAKING";
const buildConfig = (
	t: TFunction,
	data: Record<string, any>,
	location: Location,
	segmentListService: SegmentListService,
	organization: OrganizationState
) => {
	let config = {
		fields: [
			{
				id: "stocktaking_at",
				name: t("modules.stocktaking.field.stocktaking_at.title"),
				type: "date",
				render: (item: StocktakingApi) => (
					<Link to={`${location.pathname}/${item.id}`}>{FormatDate(item.stocktaking_at)}</Link>
				),
				renderExport: (item: StocktakingApi) => FormatDate(item.stocktaking_at),
			},
			{
				id: "document_number",
				name: t("modules.stocktaking.field.document_number.title"),
				type: "text",
				render: (item: StocktakingApi) => (
					<Link to={`${location.pathname}/${item.id}`}>{item.document_number}</Link>
				),
				renderExport: (item: StocktakingApi) => item.document_number,
			},
			{
				id: "stock",
				name: t("common.word.stock"),
				render: (item: StocktakingApi) => item?.stock?.name,
			},
			{
				id: "created_at",
				name: t("common.word.created_at", { ns: "lib" }),
				render: (item: StocktakingApi) => FormatDate(item.created_at),
			},
			{
				id: "type",
				name: t("common.word.type", { ns: "lib" }),
				render: (item: StocktakingApi) => t(`enums.stocktaking.type.${item.type}`),
			},
			{
				id: "stocktaking_status",
				name: t("common.word.status", { ns: "lib" }),
				render: (item: StocktakingApi) => <StocktakingStatusLabel status={item.stocktaking_status} />,
				renderExport: (item: StocktakingApi) => t(`enums.common.status.${item.stocktaking_status}`),
			},
			{
				id: "tags",
				name: t("common.word.tags"),
				render: (item: StocktakingApi) => (
					<div className="d-flex flex-wrap">
						{item?.tags?.map((tag, i) => (
							<Tag className="mb-1" key={`tag-${i}`} title={tag.name} />
						))}
					</div>
				),
				renderExport: (item: StocktakingApi) => item?.tags?.map((tag) => tag.name).join(", "),
			},
			{
				id: "comment",
				name: t("common.word.comment.title"),
				type: "text",
			},
			{
				id: "created_by",
				name: t("common.word.created_by"),
				render: (item: StocktakingApi) => item.created_by || t("common.word.system"),
			},
		],
		actions: [
			{
				name: t("common.action.edit", { ns: "lib" }),
				link: (item: StocktakingApi) => `${location.pathname}/${item.id}`,
			},
		],
		filters: [
			{
				id: "statuses",
				name: t("common.word.status", { ns: "lib" }),
				type: "list",
				listOptions: {
					DRAFT: t("enums.common.status.DRAFT"),
					OPEN: t("enums.common.status.OPEN"),
					CLOSED: t("enums.common.status.CLOSED"),
					VOID: t("enums.common.status.VOID"),
				},
			},
			{
				id: "stockIds",
				name: t("common.word.stock"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getStockSearchSelect(search, params, { cancelToken: options?.token }),
				},
			},
			{
				id: "tagQuery",
				name: t("common.word.tags"),
				type: "search_select",
				source: {
					request: (search: string, params: Record<string, any>, options?: Record<string, any>) =>
						apiOrganization.getTagSearchSelect(search, params, { cancelToken: options?.token }),
				},
			},
		],
		selectedColumns: ["stock", "document_number", "stocktaking_at", "type", "stocktaking_status", "tags"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		exportConfig: {
			title: t("modules.stocktaking.field.export_config.title"),
			filename: t("modules.stocktaking.header.title"),
			organization: organization.name,
		},
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "stock,tags,custom_fields";
			return apiOrganization.getStocktakings(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentListService.save(listName, resourceType, segment),
		numberOfStickyColumnsAtTheStart: 2,
		isNumberOfStickyColumnsDynamic: false,
		doesIdColumnRedirectToPreviewPage: true,
	} as ListConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		customFields: data.fields,
		filterValues: data.filter_values,
	};
	return config;
};

const List: FC<ListState> = ({ resource, path, mobileActions }) => {
	const { t } = useTranslation();
	const history = useHistory();
	const location = useLocation();
	const segmentContextValue = useContext(SegmentContext);
	const organization = useSelector(selectOrganization);

	if (!resource) return null;
	const data = resource.read();
	const config = buildConfig(t, data, location, segmentContextValue, organization);
	return (
		<ListData
			config={config}
			emptyList={{ addAction: () => history.push(`${path}/new`) }}
			shouldFilterAvailableMultipleActions
			mobileActions={mobileActions}
		/>
	);
};

const OrganizationStocktakingIndexPage: FC<RouteComponentProps> = (props) => {
	const { t } = useTranslation();
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "STOCKTAKING",
			name: t(`enums.custom_fields.resources.STOCKTAKING`),
		},
	];
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(t("modules.stocktaking.header.title"));
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			variant: "primary",
			path: `${props.match.url}/new`,
		},
	];
	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.add", { ns: "lib" }),
			path: `${props.match.url}/new`,
		},
	];

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.stocktaking.header.title")} buttons={buttons} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} path={props.match.url} mobileActions={mobileActions} {...props} />
			</Suspense>
		</div>
	);
};

export default OrganizationStocktakingIndexPage;
